import { vec2, vec3 } from "gl-matrix";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { undoRedoActions } from "features/undoRedo";
import { selectCanvasContextMenuFeatures, selectIsProcoreBeta, selectIsProcoreDesign } from "slices/explorer";
import { isRealVec } from "utils/misc";

import {
    renderActions,
    selectCameraType,
    selectCanvasSelectionEnabled,
    selectClippingPlanes,
    selectMainObject,
    selectPicker,
    selectSelectMultiple,
} from "../renderSlice";
import { CameraType, Picker, Stamp, StampKind } from "../types";

export function useCanvasContextMenuHandler() {
    const dispatch = useAppDispatch();
    const dispatchHighlighted = useDispatchHighlighted();
    const highlightedObjects = useHighlighted();
    const cameraType = useAppSelector(selectCameraType);
    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const features = useAppSelector(selectCanvasContextMenuFeatures);
    const picker = useAppSelector(selectPicker);
    const isMultipleSelectionActive = useAppSelector(selectSelectMultiple);
    const {
        state: { view, canvas },
    } = useExplorerGlobals();

    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isProcoreBeta = useAppSelector(selectIsProcoreBeta);
    const mainObject = useAppSelector(selectMainObject);
    const canvasSelectionEnabled = useAppSelector(selectCanvasSelectionEnabled);

    const shouldShow = isProcoreDesign ? canvasSelectionEnabled && isProcoreBeta : true;

    const handleContextMenu = async (pos: vec2, isTouch: boolean) => {
        if (!shouldShow || !view || picker !== Picker.Object || !features.length || !canvas) {
            return;
        }

        const canvasRect = canvas.getBoundingClientRect();

        const result = await view.pick(pos[0], pos[1], {
            sampleDiscRadius: isTouch ? 8 : 4,
        });

        if (!result || result.objectId === -1) {
            if (cameraType === CameraType.Orthographic && clippingPlanes.planes.length) {
                dispatch(
                    renderActions.setStamp({
                        kind: StampKind.CanvasContextMenu,
                        data: {
                            object: result?.objectId ?? undefined,
                            position: result?.position ?? undefined,
                            normal: result?.normal && isRealVec(result.normal) ? [...result.normal] : undefined,
                        },
                        pinned: true,
                        mouseX: pos[0] + canvasRect.left,
                        mouseY: pos[1] + canvasRect.top,
                    }),
                );

                return;
            } else {
                dispatch(renderActions.setStamp(null));
                return;
            }
        }

        const newStamp: Stamp = {
            kind: StampKind.CanvasContextMenu,
            data: {
                object: result.objectId,
                position: [...result.position] as vec3,
                normal: isRealVec(result.normal) ? [...result.normal] : undefined,
            },
            pinned: true,
            mouseX: pos[0] + canvasRect.left,
            mouseY: pos[1] + canvasRect.top,
        };

        const alreadySelected = highlightedObjects.ids[result.objectId];
        if (alreadySelected) {
            // If the object is already selected, just need to update the stamp
            // position. No selection changes.
            dispatch(renderActions.setStamp(newStamp));
            return;
        }

        if (isMultipleSelectionActive) {
            const selectObjectNoStamp = () => {
                dispatch(renderActions.setMainObject(result.objectId));
                dispatchHighlighted(highlightActions.add([result.objectId]));
            };
            const selectObject = () => {
                selectObjectNoStamp();
                dispatch(renderActions.setStamp(newStamp));
            };
            selectObject();

            dispatch(
                undoRedoActions.push({
                    description: "Context Menu Multiselect object",
                    undoCallback: () => {
                        dispatch(renderActions.setMainObject(mainObject));
                        dispatchHighlighted(highlightActions.remove([result.objectId]));
                    },
                    redoCallback: selectObjectNoStamp,
                }),
            );
        } else {
            const selectObjectNoStamp = () => {
                dispatch(renderActions.setMainObject(result.objectId));
                dispatchHighlighted(highlightActions.setIds([result.objectId]));
            };
            const selectObject = () => {
                selectObjectNoStamp();
                dispatch(renderActions.setStamp(newStamp));
            };
            selectObject();

            dispatch(
                undoRedoActions.push({
                    description: "Context Menu Select object",
                    undoCallback: () => {
                        dispatch(renderActions.setMainObject(mainObject));
                        dispatchHighlighted(highlightActions.setIds(highlightedObjects.idArr));
                    },
                    redoCallback: selectObjectNoStamp,
                }),
            );
        }
    };

    return handleContextMenu;
}
